// THE APP REGISTRY — one declaration per app, for the whole monorepo.
//
// Before this, the same facts were spread across SEVEN hardcoded lists that had to be
// kept in lockstep by hand:
//
//   1. plugin/app-ids.ts            PLUGIN_APP_ID_ALIASES + SYSTEM_PLUGIN_APP_IDS  (engine)
//   2. plugin/bundled-defaults.ts   BUNDLED_DEFAULT_PLUGIN_PACKAGES               (engine + prebuild) [DELETED]
//   3. plugin/index.ts              SYSTEM_PLUGIN_RUNTIMES                        (engine)
//   4. plugin/folder-discovery.ts   PLUGIN_APP_DIRS                               (engine)
//   5. plugin/builtin-scope.ts                                                    (engine)
//   6. app/src/plugins/runtime.ts   a SECOND alias map + PLUGIN_ORDER             (frontend)
//   7. SUPPORTED_PLUGIN_BLOCK_TYPES                                               (frontend)
//
// They had already drifted: the frontend alias map was missing EVERY design-studio and
// oa-computer alias, so those ids failed to canonicalize in the UI while working in the
// engine. That was a live bug.
//
// This module lives in @openanalyst-ai/core because it is the one package BOTH the
// engine and the app already depend on. Each of the seven lists is now DERIVED from
// here rather than maintained by hand. They keep their existing names and signatures —
// nothing is deleted, callers do not move.
//
// NOTE ON TITLES: an app's display name comes from its manifest.json, which is already
// the source of truth. It is deliberately NOT duplicated here.

export type AppDeclaration = {
  // The canonical id. Everything else normalizes to this.
  id: string
  // The stable product id (OA-01 …). Never reused, never reordered.
  systemId: string
  // The npm package carrying the app's folder + runtime — for the apps that still HAVE one.
  //
  // A ported sidecar app does not: everything it is lives in oa-apps/<App>, and its old
  // packages/plugin-<id> is deleted. Saying it still has a package would be a claim about the
  // tree that is simply untrue, and it is what let a build script keep looking for a folder
  // that was not there. Its historical package name lives on in `aliases`, so an
  // installed-plugin record written by an older build still resolves to the app.
  package?: string
  // The iframe bundle directory under plugin-apps/ (and resources/plugin-apps/).
  appDir: string
  // Ships with the desktop app and is materialized into a project on first discovery.
  bundled: boolean
  // Sidebar rail position. Unlisted apps sort after the ordered ones.
  order?: number
  // Every historical / package / typo id that must resolve to `id`.
  aliases: string[]
  // WHERE THE APP'S AGENT-FACING FOLDER LIVES — repo-relative path segments.
  //
  // Omitted, it derives from the package name: openanalyst-plugin-crm ->
  // packages/plugin-crm/openanalyst-plugin. That derivation used to be a hardcoded
  // string transform written out TWICE (the engine's folder-discovery and the
  // desktop's prebuild), which is exactly the kind of duplicated fact this registry
  // exists to kill.
  //
  // A SIDECAR app owns everything about itself in ONE directory — its UI, its server,
  // and its folder — so it declares that directory here instead of being forced to
  // scatter itself across packages/ to satisfy a path convention.
  folder?: readonly string[]
  // The app's directory under oa-apps/, when it runs its own backend process.
  // Set = this app has a sidecar: the shell spawns oa-apps/<sidecarDir>/server, and
  // prebuild ships that server into the package. Unset = no backend of its own.
  sidecarDir?: string
  // Does the shell mount the SIDECAR's UI, or the old plugin-apps one?
  //
  // Separate from `sidecarDir` on purpose. An app can have a working backend before its ported
  // UI carries everything the old one did — mounting a thinner rebuild would TAKE A WORKING
  // FEATURE AWAY. Such an app runs its backend but keeps its old UI until the sidecar UI is
  // ready. Dashboard is the remaining case (its real surface is the canvas with publish +
  // annotate, which its rebuild does not yet carry).
  sidecarUi?: boolean
  // Start this backend when its project becomes active, even before its iframe mounts. Use for
  // features that must work in the background (due reminders, autonomous message handlers).
  sidecarBackground?: boolean
  // The sidecar is packaged and supervised inside a hosted project runtime. This is an
  // explicit opt-in because some desktop sidecars require host capabilities or large native
  // assets that the cloud runtime does not ship.
  sidecarHosted?: boolean
  // The oa-apps directory holding this app's UI when it has NO server of its own.
  //
  // A runtime app (Generative) is isolated into oa-apps/<uiDir> like the sidecars, but it keeps
  // its in-engine TS runtime + core tools — there is no backend process to spawn. `uiDir` mounts
  // its UI from oa-apps/<uiDir>/ui/dist WITHOUT giving it a `sidecarDir` (which would make the
  // supervisor try to start a server that does not exist). Unset = mounted the normal way.
  uiDir?: string
  // DEPENDENCIES THE SIDECAR BUNDLE MUST NOT INLINE — shipped as real node_modules instead.
  //
  // prebuild bundles each sidecar's server into ONE file with `bun build`, which is correct for
  // apps whose deps are plain JS. But some packages read WASM/asset/binary files from disk relative
  // to their own install location (for example Signal runtimes, browser launchers, and native
  // image codecs). Inlining those bakes the build machine's absolute paths
  // into the bundle and breaks every install. Naming them here keeps them EXTERNAL: the bundle
  // `import`s them at runtime from a real node_modules staged next to it (npm --omit=dev, no
  // symlinks). Unset = the whole server inlines cleanly (CRM, Calendar, …).
  sidecarExternal?: readonly string[]
  // WHICH families of the shared Product Suite catalog this app may touch.
  //
  // The 10X handle exposes ~149 tools; a family is a tool's first name-segment
  // (`site_deploy` → `site`). One catalog, owned centrally (src/product-suite); each app gets
  // a SLICE of it. Generative declares only "assets", so it literally cannot call a deploy
  // tool — the engine refuses it, and the app cannot widen its own scope. Unset = the app
  // does not use the Product Suite at all.
  productSuiteFamilies?: readonly string[]
  // WHICH connected-integration providers (Composio) this app may DRIVE through the engine.
  //
  // An app that runs a provider's actions on the user's behalf — Meta driving the Marketing API
  // through the user's connected Meta account — declares that provider here. The engine's
  // /global/integrations/execute enforces it: an app can only run actions for a provider it
  // declared, so it can never reach an integration it was not built for. Case-insensitive, matched
  // against the action's provider prefix. Unset = the app drives no integrations directly.
  integrationProviders?: readonly string[]
}

export const APPS: readonly AppDeclaration[] = [
  {
    id: "dashboard",
    systemId: "OA-01",
    // No package. Dashboard is a sidecar — UI, server and folder all live in oa-apps/Dashboard.
    // It renders the project's canvas artifacts AND publishes one live through the CENTRAL
    // Product Suite (same site_deploy path as Publish — one handle, one audit trail).
    appDir: "dashboard",
    bundled: true,
    order: 0,
    aliases: ["openanalyst-plugin-dashboard"],
    folder: ["oa-apps", "Dashboard", "openanalyst-plugin"],
    sidecarDir: "Dashboard",
    sidecarUi: true,
    productSuiteFamilies: ["site", "sites", "page", "pages", "deploy", "publish", "link", "links", "form", "forms", "analytics", "domain"],
  },
  {
    id: "10x-memory",
    systemId: "OA-02",
    // No package. 10X Memory is a sidecar — UI, server and folder all live in
    // oa-apps/10x-Memory. Its old package name stays an alias for records written by an
    // older build.
    appDir: "memory",
    bundled: true,
    order: 1,
    aliases: ["10xmemory", "10x_memory", "openanalyst-plugin-10xmemory"],
    folder: ["oa-apps", "10x-Memory", "openanalyst-plugin"],
    sidecarDir: "10x-Memory",
    sidecarUi: true,
  },
  {
    id: "crm",
    systemId: "OA-03",
    // No package. CRM is a sidecar — UI, server and folder all live in oa-apps/10x-CRM, and
    // packages/plugin-crm is deleted. The old package name stays an ALIAS so a plugin record
    // written by an older build still resolves to this app.
    appDir: "crm",
    bundled: true,
    order: 2,
    aliases: ["openanalyst-plugin-crm"],
    folder: ["oa-apps", "10x-CRM", "openanalyst-plugin"],
    sidecarDir: "10x-CRM",
    sidecarUi: true,
    sidecarBackground: true,
    sidecarHosted: true,
    // commerce/forms are where the live handle actually carries CRM-relevant data today
    // (commerce_page_buyers_list = customers, forms_* = lead submissions); the contact/person
    // family names stay declared for the day the platform exposes them natively.
    productSuiteFamilies: ["contact", "contacts", "person", "company", "lead", "commerce", "forms"],
  },
  {
    id: "shopify",
    systemId: "OA-04",
    // No package. Shopify is a sidecar — UI, server and folder all live in oa-apps/10x-Shopify.
    // Its data comes from the Shopify Admin API via @oa/connector-engine (a vendor connector,
    // not the Product Suite). Its old package name stays an alias.
    appDir: "shopify",
    bundled: true,
    aliases: ["openanalyst-plugin-shopify"],
    folder: ["oa-apps", "10x-Shopify", "openanalyst-plugin"],
    sidecarDir: "10x-Shopify",
    sidecarUi: true,
  },
  {
    id: "meta-ads",
    systemId: "OA-05",
    // No package. Meta is a sidecar — UI, server and folder all live in oa-apps/10x-Meta. It calls
    // the Meta Marketing (Graph) API directly with the user's stored Marketing-API access token
    // (a multi-account token store the app owns). Its token store, selected account and data cache
    // live in its own per-project folder (<project>/output/meta-ads). Old package name kept as an
    // alias.
    appDir: "meta-ads",
    bundled: true,
    aliases: ["openanalyst-plugin-meta-ads"],
    folder: ["oa-apps", "10x-Meta", "openanalyst-plugin"],
    sidecarDir: "10x-Meta",
    sidecarUi: true,
  },
  {
    id: "whatsapp",
    systemId: "OA-06",
    // No package. WhatsApp is a sidecar — UI, server and folder all live in oa-apps/WhatsApp. Its
    // backend runs a WhatsApp session and stores chats/messages in the project's own SQLite. The
    // old package name stays an ALIAS so a plugin record written by an older build still resolves.
    appDir: "whatsapp",
    bundled: true,
    aliases: ["openanalyst-plugin-whatsapp"],
    folder: ["oa-apps", "WhatsApp", "openanalyst-plugin"],
    sidecarDir: "WhatsApp",
    sidecarUi: true,
    sidecarBackground: true,
    sidecarHosted: true,
    // Baileys and its Signal runtime load package assets from disk — keep the real package tree.
    sidecarExternal: ["@whiskeysockets/baileys", "pino", "qrcode", "sharp"],
  },
  {
    id: "10x-publish",
    systemId: "OA-07",
    // No package. 10X Publish is a sidecar — UI, server and folder all live in
    // oa-apps/10x-Publish. Its historical package names stay aliases.
    appDir: "publish",
    bundled: true,
    order: 4,
    aliases: ["10x-handle", "10xhandle", "10x_handle", "publishing", "openanalyst-plugin-publishing", "openanalyst-plugin-10x-publish", "openanalyst-plugin-10x-handle"],
    folder: ["oa-apps", "10x-Publish", "openanalyst-plugin"],
    sidecarDir: "10x-Publish",
    sidecarUi: true,
    productSuiteFamilies: ["site", "sites", "page", "pages", "deploy", "publish", "link", "links", "form", "forms", "analytics", "seo", "domain"],
  },
  {
    id: "understand",
    systemId: "OA-08",
    // Isolated into oa-apps/Understand — its agent-facing folder and its UI both live there.
    // Understand is a PURE-LOCAL project-graph scanner: its four tools (understand.scan / get /
    // summary / explain) are folder scripts the engine runs IN-PROCESS against the OPEN project —
    // no server, no account, no network, no external I/O. Like Generative it is UI-only (`uiDir`,
    // no `sidecarDir`, so the supervisor spawns nothing), but with NO in-engine runtime either, so
    // it carries no `package`. Its historical package name lives on as an alias so an
    // installed-plugin record written by an older build still resolves to this app.
    appDir: "understand",
    bundled: true,
    aliases: ["10xunderstand", "10x-understand", "10x_understand", "openanalyst-plugin-10xunderstand"],
    folder: ["oa-apps", "Understand", "openanalyst-plugin"],
    uiDir: "Understand",
  },
  {
    id: "generative",
    systemId: "OA-09",
    // Isolated into oa-apps/Generative — folder, runtime and UI all live there — but with NO
    // architectural change: it KEEPS its in-engine TS runtime (SYSTEM_PLUGIN_RUNTIMES) and the
    // engine's core generation tools. `package` stays so the engine still imports the runtime
    // in-process (oa-apps/Generative is a workspace member); `folder` relocates its agent-facing
    // folder; `uiDir` mounts its UI from oa-apps/Generative/ui with no server to spawn.
    package: "openanalyst-plugin-generative",
    appDir: "generative",
    bundled: true,
    order: 3,
    aliases: ["generative-studio"],
    folder: ["oa-apps", "Generative", "openanalyst-plugin"],
    uiDir: "Generative",
    productSuiteFamilies: ["asset", "assets", "upload", "file", "files", "media", "image"],
  },
  {
    id: "design-studio",
    systemId: "OA-10",
    // No package. Design Studio is a sidecar — UI, server and folder all live in oa-apps/Design-Studio.
    // It is a Fabric.js creative studio: multi-project designs, slide decks (PDF/PPTX export),
    // whiteboards and landing pages in its own file store under output/design-studio. Generation
    // proxies to the engine's /app-llm; there is no in-engine runtime. The old package name stays an
    // alias so a plugin record written by an older build still resolves to this app.
    appDir: "design-studio",
    bundled: true,
    aliases: ["10x-design-studio", "openanalyst-plugin-design-studio", "openanalyst-plugin-10x-design-studio"],
    folder: ["oa-apps", "Design-Studio", "openanalyst-plugin"],
    sidecarDir: "Design-Studio",
    sidecarUi: true,
    // Fabric, jspdf and pptxgenjs bundle cleanly (plain JS); the server has no native/WASM deps to
    // keep external.
  },
  {
    id: "email",
    systemId: "OA-11",
    // No package. Email is a sidecar — UI, server and folder all live in oa-apps/Email. It is an
    // end-to-end bulk-email app: templates, audiences (imported from the project's CRM leads) and
    // campaigns in its own per-project SQLite. Campaigns send through the Product Suite email tools
    // (primary, via /global/product-suite) with a connected Gmail account (Composio, via
    // /global/integrations) as the alternative sender. Old package name kept as an alias.
    appDir: "email",
    bundled: true,
    aliases: ["openanalyst-plugin-email"],
    folder: ["oa-apps", "Email", "openanalyst-plugin"],
    sidecarDir: "Email",
    sidecarUi: true,
    productSuiteFamilies: ["email", "emails", "campaign", "audience", "contact", "contacts"],
    integrationProviders: ["gmail", "googlemail", "google"],
  },
  {
    id: "share",
    systemId: "OA-13",
    // No package. Share is a sidecar — UI, server and folder all live in oa-apps/Share. It is a LAN
    // peer-to-peer file-sharing app: devices on the same Wi-Fi discover each other over mDNS and
    // browse/pull/push files device to device (no cloud). Its control API + handshake run on
    // 127.0.0.1 like every other app; a SECOND server binds 0.0.0.0 for the LAN transfers. Its
    // per-project state lives under output/share. Needs neither the engine LLM nor the Product Suite.
    appDir: "share",
    bundled: true,
    aliases: [],
    folder: ["oa-apps", "Share", "openanalyst-plugin"],
    sidecarDir: "Share",
    sidecarUi: true,
  },
  {
    id: "marketing-calendar",
    systemId: "OA-12",
    // No package. Marketing Calendar is a sidecar — UI, server and folder all live in
    // oa-apps/Marketing-Calendar. Calendar entries remain a local STORE (CRM-pattern), while
    // the Inspiration page may read approved examples from the bound handle's knowledge base.
    // It receives only the `knowledge` family — never publishing, email, contacts, or ads.
    appDir: "marketing-calendar",
    bundled: true,
    aliases: ["marketing-planner", "openanalyst-plugin-marketing-planner"],
    folder: ["oa-apps", "Marketing-Calendar", "openanalyst-plugin"],
    sidecarDir: "Marketing-Calendar",
    sidecarUi: true,
    productSuiteFamilies: ["knowledge"],
  },
  {
    id: "oa-computer",
    systemId: "OA-14",
    // No package. Computer is a sidecar — UI, server and folder all live in oa-apps/Computer-Agent.
    // Its backend owns the OS driver (behind a code-enforced safety gate) and the built-in browser
    // (headless Chromium over CDP). The old package name stays an alias for older records.
    appDir: "oa-computer",
    bundled: true,
    aliases: ["oa_computer", "oacomputer", "computer-use", "openanalyst-plugin-oa-computer"],
    folder: ["oa-apps", "Computer-Agent", "openanalyst-plugin"],
    sidecarDir: "Computer-Agent",
    sidecarUi: true,
    // sharp (native .node) and puppeteer (browser launcher) read binaries/assets from disk — never
    // inline them; ship them as real node_modules next to the bundle. @puppeteer/browsers (the
    // managed Chrome-for-Testing installer used when a machine has NO browser) arrives transitively
    // from puppeteer's own install; naming it here keeps it external so the bundle imports the real
    // package instead of inlining its extraction machinery.
    sidecarExternal: ["sharp", "puppeteer", "@puppeteer/browsers"],
  },
  {
    id: "symphony",
    systemId: "OA-15",
    // No package and NO backend of its own: Symphony's backend IS the engine's
    // /orchestrator control plane (status, workflow inspect/approve, pause/resume,
    // tickets). The UI is a static bundle in oa-apps/Symphony/ui/dist mounted like
    // Generative's (`uiDir`, no sidecar to spawn); the agent-facing folder explains
    // the app and its human-owned authority boundaries.
    appDir: "symphony",
    bundled: true,
    order: 12,
    aliases: ["orchestrator", "sca-symphony"],
    folder: ["oa-apps", "Symphony", "openanalyst-plugin"],
    uiDir: "Symphony",
  },
  {
    id: "people",
    systemId: "OA-16",
    // People is a local, serverless app. Its folder scripts read the project-local
    // people.db and the isolated UI reaches them through the standard plugin host bridge.
    // Discovery keeps it installable-but-inactive until a project explicitly installs it.
    appDir: "people",
    bundled: true,
    order: 13,
    aliases: [],
    folder: ["oa-apps", "People", "openanalyst-plugin"],
    uiDir: "People",
  },
]

// ── Derived lookups (built once) ──────────────────────────────────────────────

// Every alias → canonical id. An app's package name and its own id are always aliases
// of itself, so `openanalyst-plugin-crm`, `crm`, and `CRM` all canonicalize to "crm".
const ALIASES: Record<string, string> = (() => {
  const out: Record<string, string> = {}
  for (const app of APPS) {
    out[app.id] = app.id
    if (app.package) out[app.package] = app.id
    for (const alias of app.aliases) out[alias] = app.id
  }
  return out
})()

const BY_ID: Record<string, AppDeclaration> = Object.fromEntries(APPS.map((app) => [app.id, app]))

export function canonicalAppID(id: string): string {
  const normalized = id.trim().toLowerCase()
  return ALIASES[normalized] ?? normalized
}

export function appDeclaration(id: string): AppDeclaration | undefined {
  return BY_ID[canonicalAppID(id)]
}

export function systemAppID(id: string): string | undefined {
  return appDeclaration(id)?.systemId
}

export function sameAppID(left: string, right: string): boolean {
  return canonicalAppID(left) === canonicalAppID(right)
}

export function isAppID(id: string): boolean {
  return canonicalAppID(id) in BY_ID
}

// The alias map, for the two call sites that still want the raw record.
export function appAliases(): Record<string, string> {
  return { ...ALIASES }
}

// The Product Suite families an app may touch — its slice of the shared 149-tool catalog.
// undefined = this app does not use the Product Suite. The engine reads this to enforce
// isolation; an app cannot widen its own scope.
export function productSuiteFamiliesForApp(id: string): readonly string[] | undefined {
  return appDeclaration(id)?.productSuiteFamilies
}

// The connected-integration providers an app may DRIVE (lower-cased). The engine reads this to
// enforce that an app only runs actions for a provider it declared; an app cannot widen its own
// scope. undefined = the app drives no integrations directly.
export function integrationProvidersForApp(id: string): readonly string[] | undefined {
  return appDeclaration(id)?.integrationProviders?.map((provider) => provider.trim().toLowerCase())
}

// The apps that run their own backend process, in rail order.
// Read by the desktop supervisor (which spawns them) AND by prebuild (which ships them),
// so a new sidecar app can never be started-but-not-packaged, or packaged-but-not-started.
export function sidecarApps(): { id: string; dir: string; ui: boolean; background: boolean; external: readonly string[] }[] {
  return APPS.filter((app) => app.sidecarDir).map((app) => ({
    id: app.id,
    dir: app.sidecarDir as string,
    ui: app.sidecarUi === true,
    background: app.sidecarBackground === true,
    external: app.sidecarExternal ?? [],
  }))
}

// Hosted runtimes intentionally ship a smaller, explicitly qualified sidecar set. Keep this
// derived from the same declarations as desktop startup/packaging so the two hosts cannot drift.
export function hostedSidecarApps(): ReturnType<typeof sidecarApps> {
  const hosted = new Set(APPS.filter((app) => app.sidecarHosted === true).map((app) => app.id))
  return sidecarApps().filter((app) => hosted.has(app.id))
}

// The isolated apps that ship a UI but run NO backend of their own (Generative — it keeps its
// in-engine runtime). prebuild builds + ships their UI from oa-apps/<dir>/ui without bundling a
// server. Kept distinct from sidecarApps so the supervisor never tries to spawn a process for them.
export function uiOnlyApps(): { id: string; dir: string }[] {
  return APPS.filter((app) => app.uiDir && !app.sidecarDir).map((app) => ({ id: app.id, dir: app.uiDir as string }))
}

/**
 * Bundle-dir -> oa-apps dir, for the apps whose OWN UI the shell mounts.
 *
 * Keyed by `appDir` because that is what the app host asks with. This was a fourth hardcoded
 * app list living in folder-discovery, which is exactly the drift this registry exists to
 * end — it had already grown its own private comments about which apps were in and why.
 */
export function sidecarUiDirs(): Record<string, string> {
  return Object.fromEntries(
    APPS.filter((app) => (app.sidecarDir && app.sidecarUi) || app.uiDir).map((app) => [
      app.appDir,
      // A sidecar app mounts from its sidecarDir; a serverless runtime app (Generative) from uiDir.
      (app.sidecarDir ?? app.uiDir) as string,
    ]),
  )
}

// Where each bundled app's agent-facing folder lives, as repo-relative path segments.
// ONE definition, read by BOTH the engine's folder discovery (dev source-tree lookup)
// and the desktop's prebuild (packaging), so the two can never disagree about where an
// app's folder is.
export function bundledAppFolderSegments(): readonly (readonly string[])[] {
  return APPS.filter((app) => app.bundled).map((app) => {
    if (app.folder) return app.folder
    if (!app.package) {
      throw new Error(`App "${app.id}" has neither a package nor a folder — nothing can find its definition.`)
    }
    return ["packages", app.package.replace(/^openanalyst-/, ""), "openanalyst-plugin"]
  })
}

/**
 * The node_modules lookup — for apps that have NOT moved their folder.
 *
 * An app that declares `folder` is deliberately not where its package name says it is, and
 * probing node_modules/<package>/openanalyst-plugin would find the OLD folder anyway: the
 * workspace symlinks that path straight back to packages/<pkg>. Two folders would then claim
 * the same app id, and which one won would be down to scan order — which is exactly how you
 * ship the app the agent was migrated OFF.
 */
export function bundledAppNodeModuleSegments(): readonly (readonly string[])[] {
  return APPS.filter((app) => app.bundled && !app.folder && app.package).map(
    (app) => ["node_modules", app.package as string, "openanalyst-plugin"] as const,
  )
}

// (4) canonical id → iframe bundle directory.
export function appDirs(): Record<string, string> {
  const out: Record<string, string> = {}
  for (const app of APPS) {
    out[app.id] = app.appDir
    if (app.package) out[app.package] = app.appDir
    for (const alias of app.aliases) out[alias] = app.appDir
  }
  return out
}

// (6) sidebar rail order. Unordered apps sort after every ordered one, stably.
export function appOrder(id: string): number {
  const app = appDeclaration(id)
  return app?.order ?? APPS.length
}
